import { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  FormFeedback,
  Input,
  Label,
  Form,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { withRouter, Link, useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useResetPasswordMutation } from "../../store/api/authApi";
import { ParticlesAuth } from "../AuthenticationInner";
import { ActivityButton, AppLogo } from "../../components/Common";
import { ResetPasswordRequest } from "../../interfaces";
import LanguageDropdown from "../../components/Common/LanguageDropdown";
import { EncryptionService } from "../../services";

interface ResetPasswordForm {
  password: string;
  confirmPassword: string;
}

const ResetPasswordPageComponent = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { phone: encPhone, code: encCode } = useParams<any>();
  document.title = `${t("documentTitle", { pageName: "resetPassword" })}`;

  const [
    resetPassword,
    { isLoading: isLoadingChangePassword, isSuccess: isSuccessChangePassword },
  ] = useResetPasswordMutation();

  const [resetPasswordRequest, setResetPasswordRequest] =
    useState<ResetPasswordRequest>({
      countryCode: "",
      phoneNumber: "",
      code: "",
      password: "",
    });

  const [resetPasswordForm, setResetPasswordForm] =
    useState<ResetPasswordForm>();

  //#region form
  const form = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: resetPasswordForm?.password || "",
      confirmPassword: resetPasswordForm?.confirmPassword || "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, t("minLength", { displayName: "password", minLength: 8 }))
        .max(25, t("maxLength", { displayName: "password", maxLength: 25 }))
        .required(
          t("isNotEmpty", {
            displayName: "password",
          }),
        ),
      confirmPassword: Yup.string()
        .min(
          8,
          t("minLength", { displayName: "confirmPassword", minLength: 8 }),
        )
        .max(
          25,
          t("maxLength", { displayName: "confirmPassword", maxLength: 25 }),
        )
        .oneOf([Yup.ref("password")], t("passwordConfirmPasswordMustMatch"))
        .required(
          t("isNotEmpty", {
            displayName: "confirmPassword",
          }),
        ),
    }),

    onSubmit: (values) => {
      resetPassword({
        ...resetPasswordRequest,
        password: values.password,
      });
    },
  });
  //#endregion

  //#endregion
  useEffect(() => {
    if (encPhone && encCode) {
      try {
        const decryPhone: string = EncryptionService.decrypt(encPhone);
        const decryCode: string = EncryptionService.decrypt(encCode);
        const phoneArray: string[] = decryPhone.split("-");
        if (phoneArray && phoneArray.length === 2) {
          setResetPasswordRequest({
            ...resetPasswordRequest,
            countryCode: phoneArray[0],
            phoneNumber: phoneArray[1],
            code: decryCode,
            password: "",
          });
        }
      } catch (error) {
        history.goBack();
      }
    }
  }, [encPhone]);

  useEffect(() => {
    if (!isLoadingChangePassword && isSuccessChangePassword) {
      history.replace("/login");
    }
  }, [isLoadingChangePassword, isSuccessChangePassword]);

  return (
    <ParticlesAuth>
      <div className="auth-page-content">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">
                <CardBody className="p-4">
                  <div className="text-center mt-1">
                    <AppLogo />
                    <h5 className="text-primary mt-2">
                      {t("resetYourPassword")}
                    </h5>
                    <p className="text-muted">
                      {t("resetYourPasswordSubHeading")}
                    </p>
                  </div>
                  <div className="mt-4">
                    <div className="">
                      <div className="justify-content-center align-items-center d-flex">
                        <LanguageDropdown />
                      </div>
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          form.handleSubmit();
                          return false;
                        }}
                      >
                        <div className="mb-3">
                          <Label
                            className="form-label"
                            htmlFor="password-input"
                          >
                            {t("password")}
                          </Label>
                          <span className="text-danger">*</span>
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              autoComplete="off"
                              id="password-input"
                              name="password"
                              type="password"
                              value={form.values.password || ""}
                              className="form-control"
                              placeholder={t("enterPassword")}
                              onChange={form.handleChange}
                              onBlur={form.handleBlur}
                              invalid={
                                form.touched.password && form.errors.password
                                  ? true
                                  : false
                              }
                            />
                            {form.touched.password && form.errors.password ? (
                              <FormFeedback type="invalid">
                                {form.errors.password as string}
                              </FormFeedback>
                            ) : null}
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              id="password-addon"
                            ></button>
                          </div>
                        </div>
                        <div className="mb-3">
                          <Label
                            className="form-label"
                            htmlFor="password-input"
                          >
                            {t("confirmPassword")}
                          </Label>
                          <span className="text-danger">*</span>
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              autoComplete="off"
                              id="password-input"
                              name="confirmPassword"
                              type="password"
                              value={form.values.confirmPassword || ""}
                              className="form-control"
                              placeholder={t("enterConfirmPassword")}
                              onChange={form.handleChange}
                              onBlur={form.handleBlur}
                              invalid={
                                form.touched.confirmPassword &&
                                form.errors.confirmPassword
                                  ? true
                                  : false
                              }
                            />
                            {form.touched.confirmPassword &&
                            form.errors.confirmPassword ? (
                              <FormFeedback type="invalid">
                                {form.errors.confirmPassword as string}
                              </FormFeedback>
                            ) : null}
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              id="password-addon"
                            ></button>
                          </div>
                        </div>
                        <div className="mt-4">
                          <ActivityButton
                            title="changePassword"
                            inProgressTitle="sending"
                            color="success"
                            className="btn btn-success w-100"
                            type="submit"
                            inProgress={isLoadingChangePassword}
                            disabled={isLoadingChangePassword}
                          ></ActivityButton>
                        </div>
                      </Form>
                    </div>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-4 text-center">
                <p className="mb-0 text-white">
                  {t("backToLogin")}...{" "}
                  <Link
                    to="/login"
                    className="fw-semibold text-white text-decoration-underline"
                  >
                    {" "}
                    {t("clickHere")}{" "}
                  </Link>{" "}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export const ResetPasswordPage = withRouter(ResetPasswordPageComponent);
